import { useCallback, useRef } from 'react';
import socket from '@/components/util/socket';
import { NoteEditEventType } from '../Notes';
import { useNotesContext } from './useNotesContext';
import { formData } from '@/utils/formData';
import PublicAPI from '@/api/TreedisPublicDAO';
import { TNote } from '../types';
import { useNotesStore } from '../stores/notes.store';

export const useEditPosition = () => {
  const { showCaseTemp, tagNode, inputNodeComponent, controls, showcaseTag, tour } =
    useNotesContext();
  const { updateNote } = useNotesStore((state) => state.actions);

  // Saving initial note position
  const notePositionRef = useRef(null);
  const recentNote = useRef();

  const handleEditPosition = useCallback(
    async (note: TNote) => {
      if (note?.tagId) return;

      notePositionRef.current = { x: note.positionX, y: note.positionY, z: note.positionZ };

      const inShowcaseTemp = showCaseTemp.tag.allTags && showCaseTemp.tag.allTags.has(note.sid);
      recentNote.current = inShowcaseTemp;

      // if recently added thead it will be stored in shocaseTemp, editing will be available there
      // otherwise, tag was set in showcase
      if (inShowcaseTemp) {
        showCaseTemp.tag.startTranslateControls({
          tag: note,
          tagNode,
          inputNodeComponent,
          controls,
          cb: () => {
            const { x, y, z } = tagNode.current.obj3D.position;
            notePositionRef.current.x = x;
            notePositionRef.current.y = y;
            notePositionRef.current.z = z;
            showCaseTemp.tag.edit({ ...note, positionX: x, positionY: y, positionZ: z });
          },
        });
      } else if (showcaseTag && controls.current) {
        const intervalId = setInterval(() => {
          const threeJsTag = controls.current?.inputs?.selection?.obj3D?.children[0];
          if (threeJsTag) {
            threeJsTag.children.forEach((obj: any) => {
              obj.visible = false;
            });
            clearInterval(intervalId);
          }
        }, 10);

        await showCaseTemp.tag.startTranslateControls({
          tag: note,
          tagNode,
          inputNodeComponent,
          controls,
          cb: () => {
            const { x, y, z } = tagNode.current.obj3D.position;
            notePositionRef.current.x = x;
            notePositionRef.current.y = y;
            notePositionRef.current.z = z;
            showcaseTag.editPosition(note.sid, {
              anchorPosition: {
                x,
                y,
                z,
              },
            });
          },
        });
      }
    },
    [controls, inputNodeComponent, showCaseTemp.tag, tagNode, showcaseTag],
  );

  const handleCancleEditPosition = useCallback(
    async (note: TNote) => {
      showCaseTemp.tag.stopPositionUpdates({ controls });
      if (recentNote) {
        await showCaseTemp.tag.edit({ ...note });
      } else {
        await showcaseTag.editPosition(note.sid, {
          anchorPosition: {
            x: note.positionX,
            y: note.positionY,
            z: note.positionZ,
          },
        });
      }
      if (tagNode.current) {
        tagNode.current.stop();
        tagNode.current = undefined;
      }
      if (inputNodeComponent.current) {
        inputNodeComponent.current = undefined;
      }
      controls.current.inputs.selection = null;
      notePositionRef.current = null;
    },
    [controls, inputNodeComponent, showCaseTemp.tag, tagNode, showcaseTag],
  );

  const handleSavePosition = async (note: TNote) => {
    const noteCopy = { ...note };
    // @ts-ignore
    delete noteCopy.message;
    delete noteCopy.dummyObject;
    // @ts-ignore
    delete noteCopy.description;
    // @ts-ignore
    delete noteCopy.mediaUrls;

    const dataToSave = formData.create({
      ...noteCopy,
      positionX: notePositionRef.current.x,
      positionY: notePositionRef.current.y,
      positionZ: notePositionRef.current.z,
    });

    const result = await PublicAPI.saveThread({
      data: dataToSave,
    });

    if (result.message === 'Success') {
      const { savedThread } = result;
      showCaseTemp.tag.stopPositionUpdates({ controls });
      if (tagNode.current) {
        tagNode.current.stop();
        tagNode.current = undefined;
      }
      if (inputNodeComponent.current) {
        inputNodeComponent.current = undefined;
      }
      controls.current.inputs.selection = null;
      notePositionRef.current = null;

      socket.emit('thread_edit', {
        ...savedThread,
        room: tour.slug,
        eventType: NoteEditEventType.EditPosition,
        isRecentNote: recentNote.current,
      });
      recentNote.current = null;

      updateNote(savedThread.id, savedThread);
    }
  };

  return {
    handleEditPosition,
    handleCancleEditPosition,
    handleSavePosition,
  };
};
